import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { DENSITY_STORAGE_KEY, readStored, writeStored } from "./preferences";

// The density of the console (ADR 0016). Comfortable is the default: every target is at least 44 px. Compact is a choice of the staff
// member: table rows are 36 px and targets at least 32 px (the WCAG 2.2 minimum is 24 px). The choice sets `data-density` on the root
// element, which the `compact:` variant of the stylesheet reads, so a component does not need to know about the setting.

export type Density = "comfortable" | "compact";

type DensityContextValue = Readonly<{ density: Density; setDensity: (density: Density) => void }>;

const DensityContext = createContext<DensityContextValue>({
  density: "comfortable",
  setDensity: () => undefined,
});

function readDensity(): Density {
  return readStored(DENSITY_STORAGE_KEY) === "compact" ? "compact" : "comfortable";
}

export function DensityProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [density, setState] = useState<Density>(readDensity);

  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  const setDensity = useCallback((next: Density) => {
    setState(next);
    writeStored(DENSITY_STORAGE_KEY, next);
  }, []);

  return <DensityContext value={{ density, setDensity }}>{children}</DensityContext>;
}

/** The density the staff member chose. Outside a provider it is `comfortable`. */
export function useDensity(): DensityContextValue {
  return useContext(DensityContext);
}
